import React, { useState, useEffect } from 'react';
import { Modal, Button } from 'react-bootstrap';
import axios from '../../axios/axiosconf';

function ModalDatosFacturacion({ show, onClose, onConfirm, profesionalId, datosRips, filtroPrefijoRelajado = false }) {
    const [datosFacturacion, setDatosFacturacion] = useState({
        prefijo: '',
        numerofactura: '',
        metodo_pago: '10',
        forma_pago: 'contado'
    });

    // Estado para valores de transacción editables
    const [valoresTransaccion, setValoresTransaccion] = useState({
        valorconsulta: '0',
        descuento_consulta: '0',
        impuesto_consulta: '0',
        total_consulta: '0',
        valorprocedimiento: '0',
        descuento_procedimiento: '0',
        impuesto_procedimiento: '0',
        total_procedimiento: '0',
        total_bruto: '0',
        total_pagar: '0'
    });

    const [parametrosFev, setParametrosFev] = useState([]);
    const [loading, setLoading] = useState(false);

    // Cargar parámetros FEV cuando se abre el modal
    useEffect(() => {
        if (show && profesionalId) {
            cargarParametrosFev();
        }
    }, [show, profesionalId]);

    // Inicializar valores de transacción cuando se reciben datosRips
    useEffect(() => {
        if (show && datosRips) {
            setValoresTransaccion({
                valorconsulta: datosRips.valorconsulta || '0',
                descuento_consulta: datosRips.descuento_consulta || '0',
                impuesto_consulta: datosRips.impuesto_consulta || '0',
                total_consulta: datosRips.total_consulta || '0',
                valorprocedimiento: datosRips.valorprocedimiento || '0',
                descuento_procedimiento: datosRips.descuento_procedimiento || '0',
                impuesto_procedimiento: datosRips.impuesto_procedimiento || '0',
                total_procedimiento: datosRips.total_procedimiento || '0',
                total_bruto: datosRips.total_bruto || '0',
                total_pagar: datosRips.total_pagar || '0'
            });
        }
    }, [show, datosRips]);

    const cargarParametrosFev = async () => {
        try {
            setLoading(true);
            let userdata = JSON.parse(localStorage.getItem('userdata'));
            const config = {
                headers: { Authorization: 'Bearer ' + userdata.access_token }
            };

            const response = await axios.get('/parametros-fev?uid=' + userdata.user_id, config);
            
            if (response.status === 200) {
                setParametrosFev(response.data);
            }
        } catch (error) {
            console.error('Error cargando parámetros FEV:', error);
            alert('Error al cargar los parámetros de facturación');
        } finally {
            setLoading(false);
        }
    };

    const getPrefijosFiltrados = () => {
        const esElectronica = (p) =>
            p.tipo === 'Factura Electronica' ||
            String(p.tipo || '').toLowerCase().includes('electr');

        if (filtroPrefijoRelajado) {
            let list = parametrosFev.filter(esElectronica);
            if (list.length === 0) {
                list = parametrosFev.slice();
            }
            const mismoProf = list.filter((p) => String(p.users_id) === String(profesionalId));
            if (mismoProf.length) {
                return mismoProf.sort((a, b) => a.id - b.id);
            }
            return list.sort((a, b) => a.id - b.id);
        }

        return parametrosFev.filter(
            (param) =>
                param.tipo === 'Factura Electronica' &&
                param.users_id == profesionalId &&
                param.estado == 1
        );
    };

    const handlePrefijoChange = (e) => {
        const value = e.target.value;
        const selectedPrefijo = getPrefijosFiltrados().find(p => p.id == value);
        
        setDatosFacturacion(prev => ({
            ...prev,
            prefijo: value,
            numerofactura: selectedPrefijo ? selectedPrefijo.numero_factura : ''
        }));
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setDatosFacturacion(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleValorChange = (e) => {
        const { name, value } = e.target;
        const newValores = {
            ...valoresTransaccion,
            [name]: value
        };

        // Recalcular totales automáticamente
        const valorConsulta = parseFloat(newValores.valorconsulta) || 0;
        const porcentajeDescuentoConsulta = parseFloat(newValores.descuento_consulta) || 0;
        const porcentajeImpuestoConsulta = parseFloat(newValores.impuesto_consulta) || 0;

        const valorProcedimiento = parseFloat(newValores.valorprocedimiento) || 0;
        const porcentajeDescuentoProcedimiento = parseFloat(newValores.descuento_procedimiento) || 0;
        const porcentajeImpuestoProcedimiento = parseFloat(newValores.impuesto_procedimiento) || 0;

        // Calcular descuentos e impuestos en valores monetarios (aplicando porcentajes)
        const descuentoConsultaValor = (valorConsulta * porcentajeDescuentoConsulta) / 100;
        const impuestoConsultaValor = (valorConsulta * porcentajeImpuestoConsulta) / 100;
        
        const descuentoProcedimientoValor = (valorProcedimiento * porcentajeDescuentoProcedimiento) / 100;
        const impuestoProcedimientoValor = (valorProcedimiento * porcentajeImpuestoProcedimiento) / 100;

        // Calcular totales
        const totalConsulta = valorConsulta - descuentoConsultaValor + impuestoConsultaValor;
        const totalProcedimiento = valorProcedimiento - descuentoProcedimientoValor + impuestoProcedimientoValor;
        const totalBruto = valorConsulta + valorProcedimiento;
        const totalPagar = totalConsulta + totalProcedimiento;

        setValoresTransaccion({
            ...newValores,
            total_consulta: totalConsulta.toString(),
            total_procedimiento: totalProcedimiento.toString(),
            total_bruto: totalBruto.toString(),
            total_pagar: totalPagar.toString()
        });
    };

    const handleConfirmar = () => {
        // Validar que se haya seleccionado un prefijo
        if (!datosFacturacion.prefijo) {
            alert('Por favor seleccione un prefijo');
            return;
        }

        // Validar que se haya seleccionado un método de pago
        if (!datosFacturacion.metodo_pago) {
            alert('Por favor seleccione un método de pago');
            return;
        }

        // Combinar datos de facturación con valores de transacción actualizados
        const datosCompletos = {
            ...datosFacturacion,
            ...valoresTransaccion
        };

        // Llamar al callback con los datos completos
        onConfirm(datosCompletos);
    };

    const handleCerrar = () => {
        // Resetear el formulario
        setDatosFacturacion({
            prefijo: '',
            numerofactura: '',
            metodo_pago: '10',
            forma_pago: 'contado'
        });
        onClose();
    };

    return (
        <Modal show={show} onHide={handleCerrar} size="lg" backdrop="static">
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="fas fa-file-invoice mr-2"></i>
                    Datos de Facturación
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {loading ? (
                    <div className="text-center py-4">
                        <div className="spinner-border text-info" role="status">
                            <span className="sr-only">Cargando...</span>
                        </div>
                        <p className="mt-2">Cargando parámetros de facturación...</p>
                    </div>
                ) : (
                    <>
                        <p className="text-muted mb-3">
                            Complete los siguientes datos para generar la factura electrónica:
                        </p>

                        {/* Información de valores de transacción - EDITABLES */}
                        {datosRips && (
                            <div className="card card-info card-outline mb-3">
                                <div className="card-header">
                                    <h6 className="mb-0"><i className="fas fa-calculator mr-2"></i>Valores de la Transacción</h6>
                                </div>
                                <div className="card-body">
                                    {/* Consulta */}
                                    {datosRips.tipo_consulta && (
                                        <div className="mb-3">
                                            <h6 className="font-weight-bold text-info">Consulta</h6>
                                            <div className="row">
                                                <div className="col-md-12 mb-2">
                                                    <small className="text-muted"><strong>Código CUPS:</strong> {datosRips.cupsc || 'N/A'}</small>
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-md-4">
                                                    <div className="form-group">
                                                        <label className="small font-weight-bold">Valor Consulta ($)</label>
                                                        <input 
                                                            type="number" 
                                                            name="valorconsulta" 
                                                            className="form-control form-control-sm"
                                                            value={valoresTransaccion.valorconsulta}
                                                            onChange={handleValorChange}
                                                            step="0.01"
                                                            min="0"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-3">
                                                    <div className="form-group">
                                                        <label className="small font-weight-bold">Descuento (%)</label>
                                                        <input 
                                                            type="number" 
                                                            name="descuento_consulta" 
                                                            className="form-control form-control-sm"
                                                            value={valoresTransaccion.descuento_consulta}
                                                            onChange={handleValorChange}
                                                            step="0.01"
                                                            min="0"
                                                            max="100"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-3">
                                                    <div className="form-group">
                                                        <label className="small font-weight-bold">Impuesto (%)</label>
                                                        <input 
                                                            type="number" 
                                                            name="impuesto_consulta" 
                                                            className="form-control form-control-sm"
                                                            value={valoresTransaccion.impuesto_consulta}
                                                            onChange={handleValorChange}
                                                            step="0.01"
                                                            min="0"
                                                            max="100"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-2">
                                                    <div className="form-group">
                                                        <label className="small font-weight-bold">Total ($)</label>
                                                        <div className="bg-info text-white p-2 rounded text-center small font-weight-bold">
                                                            ${parseFloat(valoresTransaccion.total_consulta || 0).toLocaleString('es-CO')}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Procedimiento */}
                                    {datosRips.tipo_procedimiento && (
                                        <div className="mb-3">
                                            <h6 className="font-weight-bold text-info">Procedimiento</h6>
                                            <div className="row">
                                                <div className="col-md-12 mb-2">
                                                    <small className="text-muted"><strong>Código CUPS:</strong> {datosRips.cupsp || 'N/A'}</small>
                                                </div>
                                            </div>
                                            <div className="row">
                                                <div className="col-md-4">
                                                    <div className="form-group">
                                                        <label className="small font-weight-bold">Valor Procedimiento ($)</label>
                                                        <input 
                                                            type="number" 
                                                            name="valorprocedimiento" 
                                                            className="form-control form-control-sm"
                                                            value={valoresTransaccion.valorprocedimiento}
                                                            onChange={handleValorChange}
                                                            step="0.01"
                                                            min="0"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-3">
                                                    <div className="form-group">
                                                        <label className="small font-weight-bold">Descuento (%)</label>
                                                        <input 
                                                            type="number" 
                                                            name="descuento_procedimiento" 
                                                            className="form-control form-control-sm"
                                                            value={valoresTransaccion.descuento_procedimiento}
                                                            onChange={handleValorChange}
                                                            step="0.01"
                                                            min="0"
                                                            max="100"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-3">
                                                    <div className="form-group">
                                                        <label className="small font-weight-bold">Impuesto (%)</label>
                                                        <input 
                                                            type="number" 
                                                            name="impuesto_procedimiento" 
                                                            className="form-control form-control-sm"
                                                            value={valoresTransaccion.impuesto_procedimiento}
                                                            onChange={handleValorChange}
                                                            step="0.01"
                                                            min="0"
                                                            max="100"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-2">
                                                    <div className="form-group">
                                                        <label className="small font-weight-bold">Total ($)</label>
                                                        <div className="bg-info text-white p-2 rounded text-center small font-weight-bold">
                                                            ${parseFloat(valoresTransaccion.total_procedimiento || 0).toLocaleString('es-CO')}
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Totales Generales */}
                                    <hr />
                                    <div className="row">
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label className="font-weight-bold">Total Bruto</label>
                                                <div className="bg-light p-2 rounded text-center">
                                                    ${parseFloat(valoresTransaccion.total_bruto || 0).toLocaleString('es-CO')}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-6">
                                            <div className="form-group">
                                                <label className="font-weight-bold">Total a Pagar</label>
                                                <div className="bg-info text-white p-2 rounded font-weight-bold text-center">
                                                    ${parseFloat(valoresTransaccion.total_pagar || 0).toLocaleString('es-CO')}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="form-group">
                            <label>Prefijo <span className="text-danger">*</span></label>
                            <select 
                                name="prefijo" 
                                className="form-control" 
                                value={datosFacturacion.prefijo} 
                                onChange={handlePrefijoChange}
                                required
                            >
                                <option value="">Seleccionar prefijo...</option>
                                {getPrefijosFiltrados().map((p) => {
                                    const etiquetaUsuario = [p.name, p.lastname].filter(Boolean).join(' ').trim();
                                    return (
                                    <option key={p.id} value={p.id}>
                                        {p.prefijo} — {p.resolucion || etiquetaUsuario || 'Resolución FEV'}
                                    </option>
                                    );
                                })}
                            </select>
                            {getPrefijosFiltrados().length === 0 && (
                                <small className="form-text text-warning">
                                    <i className="fas fa-exclamation-triangle mr-1"></i>
                                    No hay prefijos disponibles para este profesional
                                </small>
                            )}
                        </div>

                        <div className="form-group">
                            <label>Número de Factura</label>
                            <input 
                                type="text" 
                                name="numerofactura" 
                                className="form-control bg-light" 
                                value={datosFacturacion.numerofactura}
                                readOnly
                                placeholder="Se asignará automáticamente"
                            />
                            <small className="form-text text-muted">
                                El número se asigna automáticamente según el prefijo seleccionado
                            </small>
                        </div>

                        <div className="form-group">
                            <label>Método de Pago <span className="text-danger">*</span></label>
                            <select 
                                name="metodo_pago" 
                                className="form-control" 
                                value={datosFacturacion.metodo_pago} 
                                onChange={handleInputChange}
                                required
                            >
                                <option value="10">Efectivo</option>
                                <option value="42">Crédito</option>
                                <option value="48">Tarjeta de Crédito</option>
                                <option value="49">Tarjeta de Débito</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label>Forma de Pago <span className="text-danger">*</span></label>
                            <select 
                                name="forma_pago" 
                                className="form-control" 
                                value={datosFacturacion.forma_pago} 
                                onChange={handleInputChange}
                                required
                            >
                                <option value="contado">Contado</option>
                                <option value="credito">Crédito</option>
                            </select>
                        </div>

                        <div className="alert alert-info mt-3">
                            <i className="fas fa-info-circle mr-2"></i>
                            <strong>Nota:</strong> Una vez confirmados estos datos, se generará la factura electrónica 
                            y podrá enviarla a la DIAN.
                        </div>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleCerrar}>
                    <i className="fas fa-times mr-1"></i>
                    Cancelar
                </Button>
                <Button 
                    variant="primary" 
                    onClick={handleConfirmar}
                    disabled={loading || getPrefijosFiltrados().length === 0 || !datosFacturacion.prefijo}
                >
                    <i className="fas fa-check mr-1"></i>
                    Confirmar y Facturar
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

export default ModalDatosFacturacion;
